"use client";

import NotifyMeConfirmationModal from "@/app/ec/product/[basePath]/notifyMeModalConfirmationPage";
import NotifyMeModal from "@/app/ec/product/[basePath]/notifyMeModalPage";
import { RecommendationModel } from "@/app/ec/product/recommendations/recommendations";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  RecommendationsSliceState,
  generateRecommendationsCacheKey,
  getRecommendations,
} from "@/store/recommendationsSlice";
import { AppState } from "@/store/store";
import Button from "@/components/design-system/button";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import BellIcon from "public/next-public/common/icons/bell-icon.svg";
import style from "./outOfStock.module.scss";

function OutOfStock({ mpId, className, productDetails }: OutOfStockProps) {
  const [showNotifyMe, setShowNotifyMe] = useState(false);
  const [showNotifyMeConfirmation, setShowNotifyMeConfirmation] = useState(false);
  const selectRecommendations = useMemo(() => {
    return createSelector(
      [
        (state: AppState) => state.recommendationsSlice.recommendations,
        (state: AppState, cacheKey: string) => cacheKey,
      ],
      (recommendations: RecommendationsSliceState["recommendations"], cacheKey: string) => recommendations[cacheKey],
    );
  }, []);

  const dispatch = useAppDispatch();

  const { model, objectIDs, modelParams, recommendationsCacheKey } = useMemo(() => {
    const model: RecommendationModel = "related-products";
    const objectIDs = [mpId.toString()];
    const modelParams = {
      sliderInView: true,
      maxRecommendations: 18,
      fallbackParameters: {
        filters: `general_rating >= 4 AND availability:Y`,
      },
      queryParameters: {
        filters: `general_rating >= 4.5 AND availability:Y`,
      },
    };
    const cacheKey = generateRecommendationsCacheKey(model, objectIDs, modelParams);
    return { model, objectIDs, modelParams, recommendationsCacheKey: cacheKey };
  }, [mpId]);

  const recommendations = useAppSelector((state) => selectRecommendations(state, recommendationsCacheKey));
  useEffect(() => {
    if (!recommendations) {
      dispatch(getRecommendations({ model, objectIDs, ...modelParams }));
    }
  }, [dispatch, recommendations, model, objectIDs, modelParams]);

  const closeNotifyMeModal = (isUserSubscribed: boolean) => {
    setShowNotifyMe(false);

    if (isUserSubscribed) {
      setShowNotifyMeConfirmation(true);
    }
  };

  if (productDetails) {
    return (
      <div className={`${style.OutOfStock} ${className ?? ""}`}>
        <div className={style.title}>This product is currently out of stock</div>
        <div className={style.subtitle}>Be the first to know when it comes back in stock:</div>
        <div className={style.actions}>
          <Button
            variant="secondary"
            color="blue"
            size="large"
            text="Notify Me When Available"
            leftIcon={<BellIcon width={16} height={16} aria-hidden />}
            showRightIcon={false}
            onClick={() => setShowNotifyMe(true)}
          />
          <NotifyMeModal
            mpId={mpId}
            isVisible={showNotifyMe}
            mpName={productDetails.title}
            alternativeMpIdList={recommendations?.hits.map((hit) => hit.mp_id)}
            mediaPath={productDetails.mediaPath ? productDetails.mediaPath : ""}
            url={productDetails.url}
            descr={productDetails.description}
            closeCallback={closeNotifyMeModal}
          />
          <NotifyMeConfirmationModal
            isVisible={showNotifyMeConfirmation}
            closeCallback={() => setShowNotifyMeConfirmation(false)}
          />
        </div>
      </div>
    );
  } else {
    return null;
  }
}

type OutOfStockProps = {
  className: string;
  productDetails: ProductDetails;
  mpId: number;
};

export default OutOfStock;
